import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, 
  Eye, 
  Save, 
  RefreshCw,
  Check,
  X,
  FileText,
  Gift,
  ShoppingBag,
  Bell,
  Newspaper,
  Users,
  ArrowLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EmailTemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [language, setLanguage] = useState('fr');

  const token = localStorage.getItem('ebb_token');

  const templateIcons = {
    welcome: Users,
    order_confirmation: ShoppingBag,
    invoice: FileText,
    payment_reminder: Bell,
    newsletter: Newspaper,
    gift_card: Gift,
  };

  const apiCall = async (endpoint, method = 'GET', body = null) => {
    const response = await fetch(`${BACKEND_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : null,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'API Error');
    }
    return response.json();
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/email-templates');
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handlePreview = async (templateId) => {
    setPreviewLoading(true);
    try {
      const data = await apiCall(`/api/email-templates/${templateId}/preview?language=${language}`, 'POST');
      setPreviewHtml(data.html);
      setSelectedTemplate(templateId);
    } catch (error) {
      alert(`Erreur: ${error.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-purple-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Mail className="w-10 h-10" />
                Templates Email
              </h1>
              <p className="text-purple-200 mt-2">Prévisualisez vos templates d'emails professionnels</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour à l'administration
            </button>
          </div>
        </div>

        {/* Language Selector */}
        <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4">
          <span className="font-medium text-gray-700">Langue de prévisualisation :</span>
          <div className="flex gap-2">
            {[
              { code: 'fr', label: 'Français' },
              { code: 'nl', label: 'Nederlands' }
            ].map(lang => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  language === lang.code
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template List */}
          <div className="lg:col-span-1 space-y-3">
            {loading ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-500" />
              </div>
            ) : (
              templates.map(template => {
                const Icon = templateIcons[template.id] || Mail;
                const isSelected = selectedTemplate === template.id;
                
                return (
                  <button
                    key={template.id}
                    onClick={() => handlePreview(template.id)}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      isSelected
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-white hover:bg-gray-50 shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-500' : 'bg-purple-100'}`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-purple-600'}`} />
                      </div>
                      <div>
                        <h3 className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                          {language === 'nl' ? template.name_nl : template.name}
                        </h3>
                        <p className={`text-sm ${isSelected ? 'text-purple-200' : 'text-gray-500'}`}>
                          {template.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Aperçu</h3>
                {selectedTemplate && (
                  <span className="text-sm text-purple-600 font-medium">
                    {templates.find(t => t.id === selectedTemplate)?.name}
                  </span>
                )}
              </div>
              
              {previewLoading ? (
                <div className="h-[600px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full h-[600px] border-0"
                />
              ) : (
                <div className="h-[600px] flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <Eye className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Sélectionnez un template pour voir l'aperçu</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
          <h3 className="font-semibold text-purple-800 mb-3">À propos des templates</h3>
          <ul className="space-y-2 text-sm text-purple-700">
            <li>• <strong>Bienvenue</strong> - Envoyé automatiquement à l'inscription d'un client</li>
            <li>• <strong>Confirmation de commande</strong> - Envoyé après chaque commande validée</li>
            <li>• <strong>Facture</strong> - Envoyé avec le PDF de la facture en pièce jointe</li>
            <li>• <strong>Rappel de paiement</strong> - Pour les factures impayées (1er, 2ème rappel, mise en demeure)</li>
            <li>• <strong>Newsletter</strong> - Pour les campagnes promotionnelles</li>
            <li>• <strong>Carte cadeau</strong> - Design premium avec code unique</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesPage;
